import React, { useCallback, useState } from 'react';

import {
  List,
  Datagrid,
  TextField,
  usePermissions,
  SingleFieldList,
  ChipField,
  SelectField,
  ReferenceArrayField,
  CreateButton,
  TopToolbar,
  useUpdate,
  useRecordContext,
  Button,
  useNotify,
  useRefresh,
} from 'react-admin';

import {
  Box, Card, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography,
} from '@mui/material';
import { RemoveCircleOutline } from '@mui/icons-material';

import hasAccess from '../../utilities/hasAccess';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';

import { SubListNavigation } from '../layout/SubNavigation';
import { StyledTab, StyledTabs } from '../layout/Tab';
import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import LinkField from '../layout/LinkField';
import actions, { Actions } from '../../constants/actions';
import authenticatorTypes from '../../constants/authenticatorTypes';
import useDialogStatus from '../../hooks/useDialogStatus';
import errorNotification from '../../utilities/errorNotification';
import resourceNames from '../../constants/resourceNames';

const DetachPermissionFromEntityButton = ({
  permissionId,
  permissionName,
}: {
  permissionId: string;
  permissionName: string;
}) => {
  const { open, openDialog, closeDialog } = useDialogStatus();

  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const onClickUnassignMember = useCallback(() => {
    if (!record) return;
    update(
      resourceNames.PERMISSION_ROLES,
      {
        id: `${permissionId}/detach`,
        data: { authenticators: [{ id: record.id, type: authenticatorTypes.MEMBER }] },
      },
      {
        onSuccess: () => {
          notify('Member role removed', { type: 'info' });
          refresh();
        },
        onError: (e) => {
          notify(errorNotification(e), { type: 'error' });
        },
      },
    );
  }, [notify, permissionId, record, refresh, update]);

  if (!record) return null;
  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullWidth>
        <DialogTitle fontWeight="bold">Remove member</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={6}>
            <Box display="flex" gap={1}>
              <Typography>{`You are about to remove this member from the ${permissionName} role:`}</Typography>
              <Typography fontWeight="bold">
                {`${record.firstName} ${record.lastName}`}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Typography>Are you sure you want to remove this member?</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={onClickUnassignMember} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
      <IconButton onClick={openDialog}>
        <RemoveCircleOutline />
      </IconButton>
    </>
  );
};

export const AssignedMembersDatagrid = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} empty={<NoResults variant="h6" />}>
      {hasAccess(permissions, actions.MEMBER_GET) && <LinkField sortable={false} source="id" label="ID" />}
      {!hasAccess(permissions, actions.MEMBER_GET) && <TextField sortable={false} source="id" label="ID" />}
      <TextField sortable={false} source="firstName" />
      <TextField sortable={false} source="lastName" />
      <TextField sortable={false} source="email" />
      <SelectField
        sortable={false}
        choices={[{ id: true, name: 'Active' }, { id: false, name: 'Inactive' }]}
        source="active"
        label="status"
      />
      {hasAccess(permissions, actions.MEMBER_UPDATE) && (
        <DetachPermissionFromEntityButton
          permissionId={record.id.toString()}
          permissionName={record.name}
        />
      )}
    </Datagrid>
  );
};

const MemberList = () => {
  const [activationTab, setActivationTab] = useState(0);
  const [active, setActive] = useState(true);
  const { permissions } = usePermissions<Actions[]>();

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActivationTab(() => newValue);
    setActive(() => newValue === 0);
  };
  return (
    <List
      filter={{ active }}
      empty={false}
      pagination={<Pagination disableEmptyText />}
      actions={(
        <TopToolbar>
          {hasAccess(permissions, actions.MEMBER_CREATE) && <CreateButton />}
        </TopToolbar>
      )}
      exporter={false}
    >
      <Card sx={{ margin: 0 }} variant="outlined">
        <Card sx={{ margin: 0 }}>
          <SubListNavigation>
            <StyledTabs value={activationTab} onChange={handleChange}>
              <StyledTab label="Active " />
              <StyledTab label="Inactive" />
            </StyledTabs>
          </SubListNavigation>
          <Divider />
        </Card>
        <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} empty={<NoResults variant="h6" />}>
          {hasAccess(permissions, actions.MEMBER_GET) && <LinkField sortable={false} source="id" label="ID" />}
          {!hasAccess(permissions, actions.MEMBER_GET) && <TextField sortable={false} source="id" label="ID" />}
          <TextField sortable={false} source="firstName" />
          <TextField sortable={false} source="lastName" />
          <TextField sortable={false} source="email" />
          {hasAccess(permissions, actions.PERMISSION_ROLE_LIST) && (
            <ReferenceArrayField sortable={false} source="permissionRoleIds" label="Permission roles" reference={resourceNames.PERMISSION_ROLES}>
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>
          )}
          <SelectField
            sortable={false}
            choices={[{ id: true, name: 'Active' }, { id: false, name: 'Inactive' }]}
            source="active"
            label="Status"
          />
        </Datagrid>
      </Card>
    </List>
  );
};

export default MemberList;
